<template>
    <div class="insights-content">
        <!-- 타이틀 및 탭 버튼 -->
        <div class="insights-header">
            <h3 class="insights-title">요금 분석 및 인사이트</h3>
            <div class="insights-tabs">
                <button
                    class="tab-button"
                    :class="{ active: activeTab === 0 }"
                    @click="activeTab = 0"
                    :title="'요금 구성'"
                ></button>
                <button
                    class="tab-button"
                    :class="{ active: activeTab === 1 }"
                    @click="activeTab = 1"
                    :title="'청구금액 추이'"
                ></button>
                <button
                    class="tab-button"
                    :class="{ active: activeTab === 2 }"
                    @click="activeTab = 2"
                    :title="'사용량 추이'"
                ></button>
            </div>
        </div>

        <!-- 첫 번째 탭: 기존 도넛 차트 및 통계 -->
        <div v-show="activeTab === 0" class="kepco-tab-content">
            <!-- 왼쪽: 도넛 차트 -->
            <div class="insights-left">
                <div class="donut-chart-container">
                    <div ref="donutChart" class="donut-chart"></div>
                </div>
            </div>

            <!-- 오른쪽: 주요 통계 -->
            <div class="insights-right">
                <div class="insight-section">
                    <div class="insight-label">주요 통계</div>
                    <div class="stats-grid-insight">
                        <div class="stat-item-insight">
                            <span class="stat-label-insight"
                                >{{ selectedPeriod === "monthly" ? "6개월간" : "3년간" }} 평균 청구금액</span
                            >
                            <span class="stat-value-insight">{{ formatNumber(trendStats?.avgAmount) }}원</span>
                        </div>
                        <div class="stat-item-insight">
                            <span class="stat-label-insight"
                                >{{ selectedPeriod === "monthly" ? "당월" : "당년" }} 평균 사용량</span
                            >
                            <span class="stat-value-insight">{{ formatNumber(currentPeriodAvgUsage) }} kWh</span>
                        </div>
                        <div class="stat-item-insight">
                            <span class="stat-label-insight"
                                >{{ selectedPeriod === "monthly" ? "6개월간" : "3년간" }} 최고 청구금액</span
                            >
                            <span class="stat-value-insight" :style="{ color: primaryBlueColor }"
                                >{{ formatNumber(trendStats?.maxAmount) }}원</span
                            >
                        </div>
                        <div class="stat-item-insight">
                            <span class="stat-label-insight"
                                >{{ selectedPeriod === "monthly" ? "6개월간" : "3년간" }} 최저 청구금액</span
                            >
                            <span class="stat-value-insight">{{ formatNumber(trendStats?.minAmount) }}원</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 두 번째 탭: 청구금액 Box Plot -->
        <div v-show="activeTab === 1" class="kepco-tab-content boxplot-tab">
            <div ref="billBoxPlot" class="boxplot-chart"></div>
            <div class="boxplot-right-section">
                <div class="boxplot-legend">
                    <div
                        class="legend-item priority-current"
                        :style="{ background: currentValueBgColor, borderLeftColor: currentValueColor }"
                    >
                        <div class="legend-row">
                            <div class="legend-left">
                                <span class="legend-marker current" :style="{ background: currentValueColor }"></span>
                                <span class="legend-label" :style="{ color: currentValueTextColor }">현재 금액</span>
                            </div>
                            <span class="legend-date">{{ currentBillMonthOrYearLabel }}</span>
                        </div>
                        <div class="legend-value-row" :style="{ color: currentValueTextColor }">
                            {{ formatCurrency(currentBillAmount) }}
                        </div>
                    </div>
                    <div class="legend-item">
                        <div class="legend-row">
                            <div class="legend-left">
                                <span class="legend-marker max"></span>
                                <span class="legend-label">최고 금액</span>
                            </div>
                            <span class="legend-date">{{ maxBillMonthOrYearLabel }}</span>
                        </div>
                        <div class="legend-value-row">{{ formatCurrency(maxBillAmount) }}</div>
                    </div>
                    <div class="legend-item">
                        <div class="legend-row">
                            <div class="legend-left">
                                <span class="legend-marker avg"></span>
                                <span class="legend-label">평균 금액</span>
                            </div>
                            <span class="legend-date">{{ selectedPeriod === "monthly" ? "6개월" : "3년" }}</span>
                        </div>
                        <div class="legend-value-row">{{ formatCurrency(avgBillAmount) }}</div>
                    </div>
                    <div class="legend-item">
                        <div class="legend-row">
                            <div class="legend-left">
                                <span class="legend-marker min"></span>
                                <span class="legend-label">최저 금액</span>
                            </div>
                            <span class="legend-date">{{ minBillMonthOrYearLabel }}</span>
                        </div>
                        <div class="legend-value-row">
                            {{ minBillAmount === 0 ? "0원" : formatCurrency(minBillAmount) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 세 번째 탭: 사용량 Box Plot -->
        <div v-show="activeTab === 2" class="kepco-tab-content boxplot-tab">
            <div ref="usageBoxPlot" class="boxplot-chart"></div>
            <div class="boxplot-right-section">
                <div class="boxplot-legend">
                    <div
                        class="legend-item priority-current"
                        :style="{ background: currentValueBgColor, borderLeftColor: currentValueColor }"
                    >
                        <div class="legend-row">
                            <div class="legend-left">
                                <span class="legend-marker current" :style="{ background: currentValueColor }"></span>
                                <span class="legend-label" :style="{ color: currentValueTextColor }">현재 사용량</span>
                            </div>
                            <span class="legend-date">{{ currentUsageMonthOrYearLabel }}</span>
                        </div>
                        <div class="legend-value-row" :style="{ color: currentValueTextColor }">
                            {{ formatEnergyUsage(currentUsageAmount) }}
                        </div>
                    </div>
                    <div class="legend-item">
                        <div class="legend-row">
                            <div class="legend-left">
                                <span class="legend-marker max"></span>
                                <span class="legend-label">최고 사용량</span>
                            </div>
                            <span class="legend-date">{{ maxUsageMonthOrYearLabel }}</span>
                        </div>
                        <div class="legend-value-row">{{ formatEnergyUsage(maxUsageAmount) }}</div>
                    </div>
                    <div class="legend-item">
                        <div class="legend-row">
                            <div class="legend-left">
                                <span class="legend-marker avg"></span>
                                <span class="legend-label">평균 사용량</span>
                            </div>
                            <span class="legend-date">{{ selectedPeriod === "monthly" ? "6개월" : "3년" }}</span>
                        </div>
                        <div class="legend-value-row">{{ formatEnergyUsage(avgUsageAmount) }}</div>
                    </div>
                    <div class="legend-item">
                        <div class="legend-row">
                            <div class="legend-left">
                                <span class="legend-marker min"></span>
                                <span class="legend-label">최저 사용량</span>
                            </div>
                            <span class="legend-date">{{ minUsageMonthOrYearLabel }}</span>
                        </div>
                        <div class="legend-value-row">
                            {{ minUsageAmount === 0 ? "0 kWh" : formatEnergyUsage(minUsageAmount) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as echarts from "echarts";
import { formatSignificantTwoDigitsGrouped } from "@src/utils/currencyUtils.js";
import { getPrimaryBlueColor } from "../../utils/colorUtils.js";
import { formatEnergyUsage as formatEnergyUsageUtil } from "../../utils/energyUtils.js";

export default {
    name: "DashboardInsights",
    components: {},
    props: {
        selectedPeriod: {
            type: String,
            default: "monthly",
        },
        trendStats: {
            type: Object,
            default: null,
        },
        billComposition: {
            type: Object,
            default: null,
        },
        dashboardData: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            activeTab: 0,
            donutChartInstance: null,
            billBoxPlotInstance: null,
            usageBoxPlotInstance: null,
        };
    },
    computed: {
        primaryBlueColor() {
            return getPrimaryBlueColor();
        },
        currentValueColor() {
            return "#f97316";
        },
        currentValueBorderColor() {
            return "rgba(249, 115, 22, 0.3)";
        },
        currentValueShadowColor() {
            return "rgba(249, 115, 22, 0.4)";
        },
        currentValueBgColor() {
            return "#fff7ed";
        },
        currentValueTextColor() {
            return "#c2410c";
        },
        billTrendData() {
            if (!this.dashboardData || !this.dashboardData.billTrend || !this.dashboardData.billTrend.data) {
                return [];
            }
            return this.dashboardData.billTrend.data.slice(-6); // 최근 6개월
        },
        // usageTrend 데이터를 사용한 당월/당년 평균 사용량 계산
        currentPeriodAvgUsage() {
            if (
                !this.dashboardData ||
                !this.dashboardData.usageTrend ||
                !this.dashboardData.usageTrend.data ||
                this.dashboardData.usageTrend.data.length === 0
            ) {
                return 0;
            }

            const usageData = this.dashboardData.usageTrend.data;
            // 0이 아닌 totalUsage 값들만 필터링
            const validUsages = usageData.map((item) => item.totalUsage || 0).filter((usage) => usage > 0);

            if (validUsages.length === 0) {
                return 0;
            }

            // 필터링된 데이터의 합을 필터링된 개수로 나눠서 평균 계산
            return validUsages.reduce((a, b) => a + b, 0) / validUsages.length;
        },
        maxBillMonthIndex() {
            if (!this.billTrendData || this.billTrendData.length === 0) return -1;
            let maxIndex = 0;
            let maxAmount = this.billTrendData[0]?.totalReqAmt || 0;
            this.billTrendData.forEach((item, index) => {
                const amount = item?.totalReqAmt || 0;
                if (amount > maxAmount) {
                    maxAmount = amount;
                    maxIndex = index;
                }
            });
            return maxIndex;
        },
        maxBillMonthOrYearLabel() {
            if (this.maxBillMonthIndex === -1) return "-";
            const item = this.billTrendData[this.maxBillMonthIndex];
            if (!item || !item.date) return "-";

            if (this.selectedPeriod === "monthly") {
                const year = item.date.substring(0, 4);
                const month = item.date.substring(4, 6);
                return `${year}년 ${parseInt(month)}월`;
            } else if (this.selectedPeriod === "yearly") {
                const year = item.date.substring(0, 4);
                return `${year}년`;
            }
            return "-";
        },
        maxUsageMonthOrYearLabel() {
            if (this.maxUsageMonthIndex === -1) return "-";
            const item = this.billTrendData[this.maxUsageMonthIndex];
            if (!item || !item.date) return "-";

            if (this.selectedPeriod === "monthly") {
                const year = item.date.substring(0, 4);
                const month = item.date.substring(4, 6);
                return `${year}년 ${parseInt(month)}월`;
            } else if (this.selectedPeriod === "yearly") {
                const year = item.date.substring(0, 4);
                return `${year}년`;
            }
            return "-";
        },
        maxBillAmount() {
            if (this.maxBillMonthIndex === -1) return 0;
            return this.billTrendData[this.maxBillMonthIndex]?.totalReqAmt || 0;
        },
        minBillMonthIndex() {
            if (!this.billTrendData || this.billTrendData.length === 0) return -1;
            // falsy(0, null, undefined, NaN 등) 값은 제외하고 최솟값 인덱스 계산
            const valid = this.billTrendData
                .map((item, index) => ({
                    index,
                    value: item?.totalReqAmt,
                }))
                .filter((x) => x.value);

            if (valid.length === 0) return -1;

            let minEntry = valid[0];
            valid.forEach((entry) => {
                if (entry.value < minEntry.value) {
                    minEntry = entry;
                }
            });
            return minEntry.index;
        },
        minBillMonthOrYearLabel() {
            if (this.minBillMonthIndex === -1) return "-";
            const item = this.billTrendData[this.minBillMonthIndex];
            if (!item || !item.date) return "-";

            if (this.selectedPeriod === "monthly") {
                const year = item.date.substring(0, 4);
                const month = item.date.substring(4, 6);
                return `${year}년 ${parseInt(month)}월`;
            } else if (this.selectedPeriod === "yearly") {
                const year = item.date.substring(0, 4);
                return `${year}년`;
            }
            return "-";
        },
        minBillAmount() {
            if (this.minBillMonthIndex === -1) return 0;
            return this.billTrendData[this.minBillMonthIndex]?.totalReqAmt || 0;
        },
        avgBillAmount() {
            if (!this.billTrendData || this.billTrendData.length === 0) return 0;
            // falsy(0, null, undefined, NaN 등) 값은 평균 계산에서 제외
            const values = this.billTrendData.map((item) => item?.totalReqAmt).filter((v) => v);
            if (values.length === 0) return 0;
            const total = values.reduce((sum, v) => sum + v, 0);
            return total / values.length;
        },
        currentBillAmount() {
            if (!this.billTrendData || this.billTrendData.length === 0) return 0;
            const lastItem = this.billTrendData[this.billTrendData.length - 1];
            return lastItem?.totalReqAmt || 0;
        },
        currentBillMonthOrYearLabel() {
            if (!this.billTrendData || this.billTrendData.length === 0) return "-";
            const lastItem = this.billTrendData[this.billTrendData.length - 1];
            if (!lastItem || !lastItem.date) return "-";

            if (this.selectedPeriod === "monthly") {
                const year = lastItem.date.substring(0, 4);
                const month = lastItem.date.substring(4, 6);
                return `${year}년 ${parseInt(month)}월`;
            } else if (this.selectedPeriod === "yearly") {
                const year = lastItem.date.substring(0, 4);
                return `${year}년`;
            }
            return "-";
        },
        totalBillAmount() {
            if (!this.billTrendData || this.billTrendData.length === 0) return 0;
            return this.billTrendData.reduce((sum, item) => sum + (item?.totalReqAmt || 0), 0);
        },
        maxUsageMonthIndex() {
            if (!this.billTrendData || this.billTrendData.length === 0) return -1;
            let maxIndex = 0;
            let maxUsage = this.billTrendData[0]?.totalUsage || 0;
            this.billTrendData.forEach((item, index) => {
                const usage = item?.totalUsage || 0;
                if (usage > maxUsage) {
                    maxUsage = usage;
                    maxIndex = index;
                }
            });
            return maxIndex;
        },
        maxUsageMonthLabel() {
            if (this.maxUsageMonthIndex === -1) return "-";
            const item = this.billTrendData[this.maxUsageMonthIndex];
            if (!item || !item.date) return "-";
            const year = item.date.substring(0, 4);
            const month = item.date.substring(4, 6);
            return `${year}년 ${parseInt(month)}월`;
        },
        maxUsageAmount() {
            if (this.maxUsageMonthIndex === -1) return 0;
            return this.billTrendData[this.maxUsageMonthIndex]?.totalUsage || 0;
        },
        minUsageMonthIndex() {
            if (!this.billTrendData || this.billTrendData.length === 0) return -1;
            // falsy(0, null, undefined, NaN 등) 값은 제외하고 최솟값 인덱스 계산
            const valid = this.billTrendData
                .map((item, index) => ({
                    index,
                    value: item?.totalUsage,
                }))
                .filter((x) => x.value);

            if (valid.length === 0) return -1;

            let minEntry = valid[0];
            valid.forEach((entry) => {
                if (entry.value < minEntry.value) {
                    minEntry = entry;
                }
            });
            return minEntry.index;
        },
        minUsageMonthOrYearLabel() {
            if (this.minUsageMonthIndex === -1) return "-";
            const item = this.billTrendData[this.minUsageMonthIndex];
            if (!item || !item.date) return "-";

            if (this.selectedPeriod === "monthly") {
                const year = item.date.substring(0, 4);
                const month = item.date.substring(4, 6);
                return `${year}년 ${parseInt(month)}월`;
            } else if (this.selectedPeriod === "yearly") {
                const year = item.date.substring(0, 4);
                return `${year}년`;
            }
            return "-";
        },
        minUsageAmount() {
            if (this.minUsageMonthIndex === -1) return 0;
            return this.billTrendData[this.minUsageMonthIndex]?.totalUsage || 0;
        },
        avgUsageAmount() {
            if (!this.billTrendData || this.billTrendData.length === 0) return 0;
            // falsy(0, null, undefined, NaN 등) 값은 평균 계산에서 제외
            const values = this.billTrendData.map((item) => item?.totalUsage).filter((v) => v);
            if (values.length === 0) return 0;
            const total = values.reduce((sum, v) => sum + v, 0);
            return total / values.length;
        },
        currentUsageAmount() {
            if (!this.billTrendData || this.billTrendData.length === 0) return 0;
            const lastItem = this.billTrendData[this.billTrendData.length - 1];
            return lastItem?.totalUsage || 0;
        },
        currentUsageMonthOrYearLabel() {
            if (!this.billTrendData || this.billTrendData.length === 0) return "-";
            const lastItem = this.billTrendData[this.billTrendData.length - 1];
            if (!lastItem || !lastItem.date) return "-";

            if (this.selectedPeriod === "monthly") {
                const year = lastItem.date.substring(0, 4);
                const month = lastItem.date.substring(4, 6);
                return `${year}년 ${parseInt(month)}월`;
            } else if (this.selectedPeriod === "yearly") {
                const year = lastItem.date.substring(0, 4);
                return `${year}년`;
            }
            return "-";
        },
        totalUsageAmount() {
            if (!this.billTrendData || this.billTrendData.length === 0) return 0;
            return this.billTrendData.reduce((sum, item) => sum + (item?.totalUsage || 0), 0);
        },
        billChartData() {
            if (!this.billTrendData || this.billTrendData.length === 0) return [];
            // 평균값 필드 추가
            return this.billTrendData.map((item) => ({
                ...item,
                avgBillAmount: this.avgBillAmount,
            }));
        },
        billChartConfig() {
            if (!this.billTrendData || this.billTrendData.length === 0) return [];
            return [
                {
                    name: "청구금액",
                    type: "bar",
                    dataField: "totalReqAmt",
                    color: "#cbd5e1",
                    highlightItem: this.maxBillMonthIndex,
                    highlightColor: this.primaryBlueColor,
                    borderRadius: [10, 10, 0, 0],
                    barWidth: "60%",
                    label: {
                        show: true,
                        position: "top",
                        formatter: (params) => {
                            if (params.dataIndex === this.maxBillMonthIndex) {
                                return formatSignificantTwoDigitsGrouped(params.value);
                            }
                            return "";
                        },
                        fontSize: 10,
                        fontWeight: 700,
                        color: this.primaryBlueColor,
                    },
                },
                {
                    name: "평균 청구금액",
                    type: "line",
                    dataField: "avgBillAmount",
                    color: "#94a3b8",
                    lineStyle: {
                        type: "dashed",
                        width: 2,
                    },
                    areaStyle: null,
                    symbol: "none",
                    tooltip: {
                        show: false,
                    },
                },
            ];
        },
        usageChartData() {
            if (!this.billTrendData || this.billTrendData.length === 0) return [];
            // 평균값 필드 추가
            return this.billTrendData.map((item) => ({
                ...item,
                avgUsageAmount: this.avgUsageAmount,
            }));
        },
        usageChartConfig() {
            if (!this.billTrendData || this.billTrendData.length === 0) return [];
            return [
                {
                    name: "사용량",
                    type: "bar",
                    dataField: "totalUsage",
                    color: "#cbd5e1",
                    highlightItem: this.maxUsageMonthIndex,
                    highlightColor: this.primaryBlueColor,
                    borderRadius: [10, 10, 0, 0],
                    barWidth: "60%",
                    label: {
                        show: true,
                        position: "top",
                        formatter: (params) => {
                            if (params.dataIndex === this.maxUsageMonthIndex) {
                                return formatEnergyUsageUtil(params.value);
                            }
                            return "";
                        },
                        fontSize: 10,
                        fontWeight: 700,
                        color: this.primaryBlueColor,
                    },
                },
                {
                    name: "평균 사용량",
                    type: "line",
                    dataField: "avgUsageAmount",
                    color: "#94a3b8",
                    lineStyle: {
                        type: "dashed",
                        width: 2,
                    },
                    areaStyle: null,
                    symbol: "none",
                    tooltip: {
                        show: false,
                    },
                },
            ];
        },
    },
    watch: {
        activeTab(newVal) {
            this.$nextTick(() => {
                if (newVal === 0) {
                    this.initDonutChart();
                } else if (newVal === 1) {
                    this.initBillBoxPlot();
                } else if (newVal === 2) {
                    this.initUsageBoxPlot();
                }
            });
        },
        billComposition: {
            handler() {
                if (this.activeTab === 0) {
                    this.$nextTick(() => {
                        this.updateDonutChart();
                    });
                }
            },
            deep: true,
        },
        billTrendData: {
            handler() {
                if (this.activeTab === 1) {
                    this.$nextTick(() => {
                        this.updateBillBoxPlot();
                    });
                } else if (this.activeTab === 2) {
                    this.$nextTick(() => {
                        this.updateUsageBoxPlot();
                    });
                }
            },
            deep: true,
        },
    },
    mounted() {
        setTimeout(() => {
            this.$nextTick(() => {
                if (this.activeTab === 0) {
                    this.initDonutChart();
                }
            });
        }, 1000);
        window.addEventListener("resize", this.handleChartResize);
    },
    beforeDestroy() {
        if (this.donutChartInstance) {
            this.donutChartInstance.dispose();
        }
        if (this.billBoxPlotInstance) {
            this.billBoxPlotInstance.dispose();
        }
        if (this.usageBoxPlotInstance) {
            this.usageBoxPlotInstance.dispose();
        }
        window.removeEventListener("resize", this.handleChartResize);
    },
    methods: {
        formatNumber(value) {
            const num = Number(value);
            return isNaN(num) ? "" : num.toLocaleString();
        },
        formatCurrency(value) {
            const num = Number(value);
            if (isNaN(num) || num === 0) return "-";
            return formatSignificantTwoDigitsGrouped(num);
        },
        formatEnergyUsage(value) {
            return formatEnergyUsageUtil(value);
        },
        formatPercentDiff(value) {
            const percent = value * 100;
            return percent.toFixed(1) + "%";
        },
        initDonutChart() {
            if (!this.$refs.donutChart) return;

            if (this.donutChartInstance) {
                this.donutChartInstance.dispose();
            }
            this.donutChartInstance = echarts.init(this.$refs.donutChart);
            this.updateDonutChart();
        },
        updateDonutChart() {
            if (!this.donutChartInstance || !this.billComposition) return;

            const option = {
                grid: {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    containLabel: true,
                },
                legend: {
                    show: false,
                },
                graphic: [
                    {
                        type: "group",
                        left: "center",
                        top: "center",
                        children: [
                            {
                                type: "text",
                                style: {
                                    text: "요금 구성",
                                    fontSize: 11,
                                    fontWeight: 600,
                                    fill: "#64748b",
                                    textAlign: "center",
                                },
                                y: -8,
                            },
                            {
                                type: "text",
                                style: {
                                    text: this.formatCurrency(this.billComposition.totalAmount),
                                    fontSize: 11,
                                    fontWeight: 700,
                                    fill: "#1e293b",
                                    textAlign: "center",
                                },
                                y: 8,
                            },
                        ],
                    },
                ],
                tooltip: {
                    trigger: "item",
                    appendTo: "body",
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderColor: "#e2e8f0",
                    borderWidth: 1,
                    textStyle: {
                        color: "#1e293b",
                    },
                    formatter: (params) => {
                        const value = params.value || 0;
                        const amount = formatSignificantTwoDigitsGrouped(value);
                        const percent = params.percent ? params.percent.toFixed(1) + "%" : "0%";

                        let title = `<div style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-weight: 700; font-size: 12px; color: #1e293b;">${echarts.format.encodeHTML(params.name)}</div>`;
                        let body = '<div style="padding: 8px 12px;">';
                        const items = [
                            { label: "금액", value: amount },
                            { label: "비율", value: percent },
                        ];
                        items.forEach((item) => {
                            body += `<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; font-size: 11px;">
                                <span style="color: #64748b;">${item.label}</span>
                                <span style="font-weight: 700; color: #1e293b; margin-left: 16px;">${item.value}</span>
                            </div>`;
                        });
                        body += "</div>";
                        return title + body;
                    },
                },
                series: [
                    {
                        name: "요금 구성",
                        type: "pie",
                        radius: ["40%", "70%"],
                        center: ["50%", "50%"],
                        avoidLabelOverlap: false,
                        itemStyle: {
                            borderRadius: 4,
                            borderColor: "#fff",
                            borderWidth: 2,
                        },
                        label: {
                            show: true,
                            position: "outside",
                            formatter: "{b}\n{d}%",
                            fontSize: 10,
                            fontWeight: 600,
                            color: "#1e293b",
                        },
                        labelLine: {
                            show: true,
                            length: 15,
                            length2: 10,
                            lineStyle: {
                                color: "#64748b",
                                width: 1,
                                type: "solid",
                            },
                        },

                        data: [
                            {
                                value: this.billComposition.totalBase || 0,
                                name: "기본요금",
                                itemStyle: { color: "#64748b" },
                            },
                            {
                                value: this.billComposition.totalKwh || 0,
                                name: "전력량요금",
                                itemStyle: { color: this.primaryBlueColor },
                            },
                            {
                                value: this.billComposition.totalOther || 0,
                                name: "기타요금",
                                itemStyle: { color: "#94a3b8" },
                            },
                            {
                                value: this.billComposition.totalTax || 0,
                                name: "부가세 및 전력기금",
                                itemStyle: { color: "#cbd5e1" },
                            },
                        ],
                    },
                ],
            };

            this.donutChartInstance.setOption(option);
        },
        billYAxisFormatter(value) {
            if (value >= 100000000) {
                return (value / 100000000).toFixed(1) + "억";
            } else if (value >= 10000) {
                return (value / 10000).toFixed(0) + "만";
            }
            return value.toLocaleString();
        },
        usageYAxisFormatter(value) {
            return formatEnergyUsageUtil(value);
        },
        handleChartResize() {
            if (this.donutChartInstance) {
                this.donutChartInstance.resize();
            }
            if (this.billBoxPlotInstance) {
                this.billBoxPlotInstance.resize();
            }
            if (this.usageBoxPlotInstance) {
                this.usageBoxPlotInstance.resize();
            }
        },
        initBillBoxPlot() {
            if (!this.$refs.billBoxPlot) return;

            if (this.billBoxPlotInstance) {
                this.billBoxPlotInstance.dispose();
            }
            this.billBoxPlotInstance = echarts.init(this.$refs.billBoxPlot);
            this.updateBillBoxPlot();
        },
        createBoxPlotOption(config) {
            const { boxplotData, maxValue, avgValue, currentValue, minValue, formatValue, zeroValueText, yMin, yMax } =
                config;

            return {
                tooltip: {
                    show: false,
                },
                grid: {
                    left: "5%",
                    right: "5%",
                    top: "10%",
                    bottom: "15%",
                },
                xAxis: {
                    type: "category",
                    data: [""],
                    show: false,
                },
                yAxis: {
                    type: "value",
                    show: false,
                    // 데이터 범위에 맞춰 스케일링하여 세로 공간을 최대한 활용
                    min: typeof yMin === "number" ? yMin : null,
                    max: typeof yMax === "number" ? yMax : null,
                    scale: true,
                },
                series: [
                    {
                        name: "분포",
                        type: "boxplot",
                        data: boxplotData,
                        silent: true,
                        boxWidth: ["30%", "60%"],
                        itemStyle: {
                            // 모던한 그라디언트 (상단 밝게 -> 하단 어두워지는 입체감)
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: "#e0e7ef" },
                                { offset: 0.5, color: "#cbd5e1" },
                                { offset: 1, color: "#b8c4d0" },
                            ]),
                            // 테두리를 더 두껍고 부드럽게
                            borderColor: "rgba(148, 163, 184, 0.6)",
                            borderWidth: 2.5,
                            borderType: "solid",

                            // 더 부드러운 그림자로 둥근 느낌 강화
                            shadowColor: "rgba(100, 116, 139, 0.3)",
                            shadowBlur: 20,
                            shadowOffsetX: 0,
                            shadowOffsetY: 6,
                        },
                        emphasis: {
                            disabled: true, // hover 효과 완전히 비활성화
                        },
                    },
                    {
                        name: "최고값",
                        type: "scatter",
                        data: [[0, maxValue]],
                        symbol: "circle",
                        symbolSize: 14,
                        itemStyle: {
                            color: "#1e293b",
                            // 링 스타일 효과를 위한 외곽선
                            borderColor: "rgba(30, 41, 59, 0.3)",
                            borderWidth: 3,
                            shadowColor: "rgba(30, 41, 59, 0.4)",
                            shadowBlur: 12,
                        },
                        label: {
                            show: true,
                            position: "top",
                            formatter: (params) => {
                                const value = params.value[1];
                                const formatted = formatValue(value);
                                return `최고 ${formatted}`;
                            },
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#1e293b",
                            // 라벨에 반투명 배경 패널 추가로 가독성 향상
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            padding: [5, 9],
                            borderRadius: 6,
                            shadowColor: "rgba(0, 0, 0, 0.08)",
                            shadowBlur: 8,
                            shadowOffsetY: 2,
                        },
                    },
                    {
                        name: "평균값",
                        type: "scatter",
                        data: [[0, avgValue]],
                        symbol: "circle",
                        symbolSize: 14,
                        itemStyle: {
                            color: "#64748b",
                            borderColor: "rgba(100, 116, 139, 0.3)",
                            borderWidth: 3,
                            shadowColor: "rgba(100, 116, 139, 0.3)",
                            shadowBlur: 12,
                        },
                        label: {
                            show: true,
                            position: "top",
                            formatter: (params) => {
                                const value = params.value[1];
                                const formatted = formatValue(value);
                                return `평균 ${formatted}`;
                            },
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#64748b",
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            padding: [5, 9],
                            borderRadius: 6,
                            shadowColor: "rgba(0, 0, 0, 0.08)",
                            shadowBlur: 8,
                            shadowOffsetY: 2,
                        },
                    },
                    {
                        name: "최저값",
                        type: "scatter",
                        data: [[0, minValue]],
                        symbol: "circle",
                        symbolSize: 14,
                        itemStyle: {
                            color: "#94a3b8",
                            borderColor: "rgba(148, 163, 184, 0.3)",
                            borderWidth: 3,
                            shadowColor: "rgba(148, 163, 184, 0.3)",
                            shadowBlur: 12,
                        },
                        label: {
                            show: true,
                            position: "bottom",
                            formatter: (params) => {
                                // const value = params.value[1];
                                const formatted = params.value[1] === 0 ? zeroValueText : formatValue(params.value[1]);
                                return `최저 ${formatted}`;
                            },
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#94a3b8",
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            padding: [5, 9],
                            borderRadius: 6,
                            shadowColor: "rgba(0, 0, 0, 0.08)",
                            shadowBlur: 8,
                            shadowOffsetY: 2,
                        },
                    },
                    {
                        name: "현재값",
                        type: "scatter",
                        data: [[0, currentValue]],
                        symbol: "circle",
                        symbolSize: 14,
                        itemStyle: {
                            color: this.currentValueColor,
                            // 링 스타일 효과를 위한 외곽선
                            borderColor: this.currentValueBorderColor,
                            borderWidth: 3,
                            shadowColor: this.currentValueShadowColor,
                            shadowBlur: 12,
                        },
                        label: {
                            show: true,
                            position: "bottom",
                            formatter: (params) => {
                                const value = params.value[1];
                                const formatted = formatValue(value);
                                return `현재 ${formatted}`;
                            },
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#ffffff",
                            backgroundColor: this.currentValueColor,
                            padding: [5, 9],
                            borderRadius: 6,
                            shadowColor: "rgba(0, 0, 0, 0.08)",
                            shadowBlur: 8,
                            shadowOffsetY: 2,
                        },
                    },
                ],
            };
        },
        updateBillBoxPlot() {
            if (!this.billBoxPlotInstance || !this.billTrendData || this.billTrendData.length === 0) return;

            // falsy(0, null, undefined, NaN 등) 값은 박스플롯 계산에서 제외
            let values = this.billTrendData
                .map((item) => item.totalReqAmt)
                .filter((v) => v)
                .sort((a, b) => a - b);

            // 유효한 값이 없다면 0 기준으로 박스플롯 생성
            if (values.length === 0) {
                values = [0];
            }

            const min = values[0];
            const max = values[values.length - 1];

            // y축 스케일링을 위한 최소/최대 값에 약간의 여백(padding) 적용
            const range = max - min;
            let padding = 0;
            if (range === 0) {
                padding = min === 0 ? 1 : Math.abs(min) * 0.1;
            } else {
                padding = range * 0.1;
            }
            const yMin = Math.max(0, min - padding);
            const yMax = max + padding;
            const q1Index = Math.floor(values.length * 0.25);
            const medianIndex = Math.floor(values.length * 0.5);
            const q3Index = Math.floor(values.length * 0.75);
            const q1 = values[q1Index] || 0;
            const median = values[medianIndex] || 0;
            const q3 = values[q3Index] || 0;

            const boxplotData = [[min, q1, median, q3, max]];

            const option = this.createBoxPlotOption({
                dataField: "totalReqAmt",
                boxplotData,
                maxValue: this.maxBillAmount,
                avgValue: this.avgBillAmount,
                currentValue: this.currentBillAmount,
                minValue: this.minBillAmount,
                formatValue: (value) => this.formatCurrency(value),
                formatBoxplotValue: (value) => this.formatCurrency(value),
                zeroValueText: "0원",
                yMin,
                yMax,
            });

            this.billBoxPlotInstance.setOption(option);
        },
        initUsageBoxPlot() {
            if (!this.$refs.usageBoxPlot) return;

            if (this.usageBoxPlotInstance) {
                this.usageBoxPlotInstance.dispose();
            }
            this.usageBoxPlotInstance = echarts.init(this.$refs.usageBoxPlot);
            this.updateUsageBoxPlot();
        },
        updateUsageBoxPlot() {
            if (!this.usageBoxPlotInstance || !this.billTrendData || this.billTrendData.length === 0) return;

            // falsy(0, null, undefined, NaN 등) 값은 박스플롯 계산에서 제외
            let values = this.billTrendData
                .map((item) => item.totalUsage)
                .filter((v) => v)
                .sort((a, b) => a - b);

            // 유효한 값이 없다면 0 기준으로 박스플롯 생성
            if (values.length === 0) {
                values = [0];
            }

            const min = values[0];
            const max = values[values.length - 1];

            // y축 스케일링을 위한 최소/최대 값에 약간의 여백(padding) 적용
            const range = max - min;
            let padding = 0;
            if (range === 0) {
                padding = min === 0 ? 1 : Math.abs(min) * 0.1;
            } else {
                padding = range * 0.1;
            }
            const yMin = Math.max(0, min - padding);
            const yMax = max + padding;
            const q1Index = Math.floor(values.length * 0.25);
            const medianIndex = Math.floor(values.length * 0.5);
            const q3Index = Math.floor(values.length * 0.75);
            const q1 = values[q1Index] || 0;
            const median = values[medianIndex] || 0;
            const q3 = values[q3Index] || 0;

            const boxplotData = [[min, q1, median, q3, max]];

            const option = this.createBoxPlotOption({
                dataField: "totalUsage",
                boxplotData,
                maxValue: this.maxUsageAmount,
                avgValue: this.avgUsageAmount,
                currentValue: this.currentUsageAmount,
                minValue: this.minUsageAmount,
                formatValue: (value) => this.formatEnergyUsage(value),
                formatBoxplotValue: (value) => this.formatEnergyUsage(value),
                zeroValueText: "0 kWh",
                yMin,
                yMax,
            });

            this.usageBoxPlotInstance.setOption(option);
        },
    },
};
</script>

<style scoped>
.insights-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

/* 헤더 (타이틀 + 탭 버튼) */
.insights-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 12px 8px 12px;
    flex-shrink: 0;
}

.insights-title {
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

/* 탭 버튼 */
.insights-tabs {
    display: flex;
    gap: 6px;
}

.tab-button {
    width: 15px;
    height: 15px;
    border-radius: 50%;
    border: none;
    background: #cbd5e1;
    cursor: pointer;
    padding: 0;
    transition: all 0.2s ease;
    outline: none;
}

.tab-button:hover {
    background: #94a3b8;
    transform: scale(1.1);
}

.tab-button.active {
    background: rgb(59, 130, 246);
    transform: scale(1.2);
}

/* 탭 컨텐츠 */
.kepco-tab-content {
    flex: 1;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    padding: 0px 16px;
    min-height: 0;
    align-items: stretch;
    /* overflow: hidden; */
}

/* 두 번째/세 번째 탭: Box Plot */
.boxplot-tab {
    grid-template-columns: 1fr 1fr;
    /* padding: 16px; */
    align-items: stretch;
    gap: 16px;
}

.boxplot-chart {
    width: 100%;
    height: 100%;
    min-height: 0;
}

.boxplot-right-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 0;
}

.boxplot-legend {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-height: 0;
    justify-content: center;
}

.legend-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 10px;
    background: #f8fafc;
    border-radius: 6px;
    border-left: 3px solid #e2e8f0;
    flex-shrink: 0;
}

.legend-item.priority-current {
    /* 색상은 :style로 동적 적용 */
}

.legend-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.legend-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
}

.legend-label {
    font-size: 10px;
    font-weight: 600;
    color: #64748b;
    white-space: nowrap;
}

.legend-date {
    font-size: 10px;
    font-weight: 500;
    color: #94a3b8;
    white-space: nowrap;
}

.legend-value-row {
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
    padding-left: 20px;
}

.priority-current .legend-label {
    /* 색상은 :style로 동적 적용 */
    font-weight: 700;
}

.priority-current .legend-value-row {
    /* 색상은 :style로 동적 적용 */
}

/* 마커 스타일 */
.legend-marker {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
}

.legend-marker.current {
    /* 색상은 :style로 동적 적용 */
}

.legend-marker.max {
    background: #1e293b;
}

.legend-marker.avg {
    background: #64748b;
}

.legend-marker.min {
    background: #94a3b8;
}

/* 두 번째/세 번째 탭: 비교 카드 */
.comparison-tab {
    grid-template-columns: 1fr;
    padding: 20px;
    align-items: stretch;
}

.comparison-container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    width: 100%;
    height: 100%;
}

.comparison-card {
    display: flex;
    flex-direction: column;
    padding: 20px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
}

.comparison-card:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
}

.comparison-card.current {
    border: 2px solid #3b82f6;
    background: #f8fafc;
}

.comparison-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
}

.comparison-label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.comparison-period {
    font-size: 10px;
    color: #94a3b8;
    font-weight: 600;
}

.comparison-value {
    font-size: 20px;
    font-weight: 800;
    color: #1e293b;
    margin-bottom: 16px;
    line-height: 1.2;
}

.comparison-value.above-avg {
    color: #3b82f6;
}

.comparison-bar {
    width: 100%;
    height: 6px;
    background: #e2e8f0;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 8px;
}

.comparison-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%);
    border-radius: 3px;
    transition: width 0.3s ease;
}

.comparison-bar-fill.avg {
    background: linear-gradient(90deg, #64748b 0%, #94a3b8 100%);
}

.comparison-bar-fill.current {
    background: linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%);
}

.comparison-diff {
    margin-top: auto;
    padding-top: 8px;
}

.diff-badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
}

.diff-badge.above {
    background: #dbeafe;
    color: #1e40af;
}

.diff-badge.below {
    background: #f1f5f9;
    color: #475569;
}

/* 첫 번째 탭: 왼쪽 도넛 차트 */
.insights-left {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    min-height: 0;
    align-self: stretch;
    overflow: hidden;
}

.donut-chart-container {
    position: relative;
    width: 100%;
    flex: 1;
    min-height: 250px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.donut-chart {
    width: 100%;
    height: 260px;
}

/* 첫 번째 탭: 오른쪽 주요 통계 */
.insights-right {
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.insight-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.insight-label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* 통계 그리드 */
.stats-grid-insight {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    flex: 1;
    min-height: 0;
}

.stat-item-insight {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 10px;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    min-height: 0;
    position: relative;
}

.stat-label-insight {
    font-size: 10px;
    color: #64748b;
    font-weight: 600;
    align-self: flex-start;
}

.stat-value-insight {
    font-size: 13px;
    color: #1e293b;
    font-weight: 700;
    align-self: flex-end;
    margin-top: auto;
}

.stat-value-insight.highlight {
    color: #3b82f6;
}

/* 두 번째/세 번째 탭: 차트 컨테이너 */
.bill-chart-container,
.usage-chart-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.bill-bar-chart,
.usage-bar-chart {
    width: 100%;
    height: 100%;
    /* min-height: 200px; */
}

/* 요약 섹션 */
.bill-summary-section,
.usage-summary-section {
    display: flex;
    flex-direction: column;
    padding: 12px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    min-height: 0;
}

.summary-title {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
}

.summary-content {
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
}

.summary-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
    background: #ffffff;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

.summary-label {
    font-size: 10px;
    color: #64748b;
    font-weight: 600;
}

.summary-value {
    font-size: 12px;
    color: #1e293b;
    font-weight: 700;
}

.summary-value.highlight-text {
    color: #3b82f6;
}
</style>
